import type { DatePickerProps } from '@components/moleculs/date-pickers/date-picker/types';
import MuiFormHelperText from '@mui/material/FormHelperText';
import styled from 'styled-components';

import 'react-datepicker/dist/react-datepicker.css';

type InputProps = {
    $inputSize: DatePickerProps['inputSize'];
    $error?: boolean;
    $helperTextStatic?: boolean;
};

export const DatePickerContainer = styled.div<InputProps>`
    position: relative;

    &.datepicker-open {
        .react-datepicker-wrapper {
            .react-datepicker__input-container {
                border-color: ${(props) =>
                    props.$error ? props.theme.palette.secondaryA4 : props.theme.palette.primaryA1};

                &:hover {
                    border-color: ${(props) =>
                        props.$error
                            ? props.theme.palette.secondaryA4
                            : props.theme.palette.primaryA1};
                }
            }
        }
    }

    .react-datepicker-wrapper {
        width: 100%;

        .react-datepicker__input-container {
            &:hover {
                border: 1px solid ${(props) => props.theme.palette.primaryA2};
            }
            cursor: text;
            transition: all 0.3s;

            box-shadow: none;
            border-color: ${(props) =>
                props.$error ? props.theme.palette.secondaryA4 : props.theme.palette.primaryA4};
            border-width: 1px;
            border-style: solid;
            background-color: ${(props) => props.theme.palette.primaryA7} !important;
            border-radius: 8px;
            ${(props) => props.theme.typography.smallTextBold}
            ${(props) => {
                switch (props.$inputSize) {
                    // border 1px and box-sizing: border-box, decreese padding by 1px
                    case 's':
                        return {
                            padding: '8px 12px',
                            minHeight: 32,
                            maxHeight: 32,
                        };
                    case 's/m':
                        return {
                            padding: '12',
                            minHeight: 40,
                            maxHeight: 40,
                        };

                    case 'xl':
                        return {
                            padding: '13px 12px',
                            minHeight: 60,
                            maxHeight: 60,
                        };
                    case 'm':
                    default:
                        return {
                            padding: '10px 12px',
                            minHeight: 44,
                            maxHeight: 44,
                        };
                }
            }};
        }
    }

    .datepicker-input {
        ${(props) => {
            switch (props.$inputSize) {
                case 'xl':
                    return props.theme.typography.bigTextRegular;
                case 'm':
                    return props.theme.typography.textBold;
                default:
                    return props.theme.typography.smallTextBold;
            }
        }};
        color: ${(props) => props.theme.palette.textMain};
        padding: 0;
        background-color: ${(props) => props.theme.palette.primaryA7};
        border: none;
        width: 100%;

        &::placeholder {
            color: ${(props) => props.theme.palette.textLight};
        }

        &:disabled {
            cursor: default;
            // same as Mui uses for disabled state
            color: ${(props) => props.theme.palette.primaryA3};
        }
    }

    .react-datepicker__close-icon {
        display: flex;
        align-items: center;
        right: 32px;

        &:hover {
            .clear-icon {
                color: ${(props) => props.theme.palette.primaryA1};
            }
        }
    }

    &.datepicker-no-icon {
        .react-datepicker__close-icon {
            right: 6px;
        }
    }
    .clear-icon {
        transition: all 0.3s;
        width: 16px;
        height: 16px;
        color: ${(props) => props.theme.palette.primaryA3};
    }

    .react-datepicker__close-icon::after {
        display: none;
    }
    .react-datepicker__calendar-icon {
        width: auto;
        height: auto;
        padding: 8px !important;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 2px;
    }

    .calendar-icon {
        color: ${(props) => props.theme.palette.primaryA2};
    }
`;

export const FormHelperText = styled(MuiFormHelperText)<Pick<InputProps, '$helperTextStatic'>>`
    ${(props) => props.theme.typography.smallTextBold}
    color: ${(props) => props.theme.palette.secondaryA4};
    margin: 3px 14px 0;

    ${(props) => {
        if (!props.$helperTextStatic) {
            return {
                position: 'absolute',
                left: 0,
                width: 'max-content',
                bottom: '-20px',
            };
        }

        return {
            alignSelf: 'start',
        };
    }}

    .helper-container {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .hint {
            ${(props) => props.theme.typography.smallTextBold}
            color: ${(props) => props.theme.palette.textLight};
        }
    }
`;
